import React, { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, LoaderCircle, ArrowLeft, Chrome } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "../components/Logo";
import { api, setToken, formatApiError } from "../lib/api";

const LoginPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState(location.state?.error || "");
    const [loading, setLoading] = useState(false);

    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const googleLogin = () => {
        const redirectUrl = window.location.origin + "/settings";
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    };

    const submit = async (e) => {
        e.preventDefault();
        if (loading) return;
        setLoading(true);
        setError("");
        try {
            const { data } = await api.post("/auth/login", { email, password });
            setToken(data.token);
            toast.success("Welcome back.");
            navigate("/settings");
        } catch (err) {
            setError(formatApiError(err, "Login failed."));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="flex min-h-screen items-center justify-center bg-[#F5F2EA] px-4">
            <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                className="w-full max-w-[400px] rounded-[20px] border border-[#E4DFD3] bg-[#FDFCF8] p-8 shadow-[0_24px_60px_-24px_rgba(34,37,34,0.25)]"
            >
                <div className="flex justify-center">
                    <Logo />
                </div>
                <h1 className="mt-8 text-center font-serif text-2xl font-semibold text-[#1A1D1A]">Owner login</h1>
                <p className="mt-2 text-center text-xs text-[#6D736D]">Site administration — authorized access only.</p>

                <form onSubmit={submit} className="mt-8 space-y-4">
                    <div>
                        <label htmlFor="login-email" className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-[#6D736D]">Email</label>
                        <input
                            id="login-email"
                            data-testid="login-username-input"
                            type="email"
                            required
                            autoComplete="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="h-11 w-full rounded-xl border border-[#E4DFD3] bg-white px-4 text-sm text-[#1A1D1A] outline-none transition-colors focus:border-[#576856]"
                        />
                    </div>
                    <div>
                        <label htmlFor="login-password" className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-[#6D736D]">Password</label>
                        <input
                            id="login-password"
                            data-testid="login-password-input"
                            type="password"
                            required
                            autoComplete="current-password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="h-11 w-full rounded-xl border border-[#E4DFD3] bg-white px-4 text-sm text-[#1A1D1A] outline-none transition-colors focus:border-[#576856]"
                        />
                    </div>
                    {error && (
                        <p data-testid="login-error-message" className="rounded-lg border border-[#A65656]/30 bg-[#A65656]/10 px-3 py-2 text-xs font-medium text-[#A65656]">
                            {error}
                        </p>
                    )}
                    <button
                        data-testid="login-submit-btn"
                        type="submit"
                        disabled={loading}
                        className="flex h-11 w-full items-center justify-center gap-2 rounded-full bg-[#576856] text-sm font-semibold text-white transition-all duration-300 hover:bg-[#1A1D1A] active:scale-[0.98] disabled:opacity-70"
                    >
                        {loading ? <LoaderCircle size={16} className="animate-spin" /> : <Lock size={15} strokeWidth={1.8} />}
                        Sign in
                    </button>
                </form>

                <div className="my-5 flex items-center gap-3">
                    <span className="h-px flex-1 bg-[#E4DFD3]" />
                    <span className="text-[11px] font-semibold uppercase tracking-wider text-[#6D736D]">or</span>
                    <span className="h-px flex-1 bg-[#E4DFD3]" />
                </div>

                <button
                    data-testid="google-login-btn"
                    onClick={googleLogin}
                    className="flex h-11 w-full items-center justify-center gap-2.5 rounded-full border border-[#C4BCA8] bg-white text-sm font-semibold text-[#1A1D1A] transition-all duration-300 hover:border-[#576856] hover:bg-[#F5F2EA] active:scale-[0.98]"
                >
                    <Chrome size={16} strokeWidth={1.8} />
                    Sign in with Google
                </button>
                <p className="mt-3 text-center text-[11px] text-[#6D736D]">Google sign-in works only for the owner account ({`kibale3@gmail.com`}).</p>

                <Link to="/" className="mt-6 flex items-center justify-center gap-1.5 text-xs font-medium text-[#6D736D] transition-colors hover:text-[#1A1D1A]">
                    <ArrowLeft size={13} /> Back to the guide
                </Link>
            </motion.div>
        </div>
    );
};

export default LoginPage;
